import React from "react";
import {
  currencyFromUrl,
  isTier1UkRestricted,
} from "@funds/features/helpers/functions";
import Header from "@appComponents/Header/Header";
import { withRouter, Link } from "react-router-dom";
import { EActionTypes } from "@funds/enums/EActionTypes";
import { IManageHeader } from "./IManageHeader";
import Dropdown from "@appComponents/Dropdown/Dropdown";
import CurrencyElement from "@appComponents/CurrencyElement/CurrencyElement";
import { IWalletDetails } from "@market/interfaces/IWallet";
import { classNames } from "@features/utils/reactFunctions";
import { FundsAppContext } from "@features/context";
import { TDropdownDetails } from "@appComponents/Dropdown/IDropdown";
import { MAIN_CURRENCY, MAIN_CURRENCY_LABEL } from "@bundles/consts";

import * as style from "./ManageHeaderStyles.module.scss"

export const cleanUrlFromAction = (url: string, action?: string) => {
  if (!action) return url;
  let cleanedUrl;
  cleanedUrl = url.split("/");
  const removedAction = cleanedUrl.pop();
  if (removedAction !== action) return url;
  cleanedUrl = cleanedUrl.join("/");

  return cleanedUrl;
};

const getDropdownData = (tier?: string): TDropdownDetails => ({
  [EActionTypes.buy]: {
    name: "Buy",
    slug: EActionTypes.buy,
    id: 1,
    active: true,
  },
  [EActionTypes.deposit]: {
    name: "Deposit",
    slug: EActionTypes.deposit,
    id: 2,
    active: !isTier1UkRestricted(tier ?? ""),
  },
  [EActionTypes.withdraw]: {
    name: "Withdraw",
    slug: EActionTypes.withdraw,
    id: 3,
    active: true,
  },
  [EActionTypes.sell]: {
    name: "Sell",
    slug: EActionTypes.sell,
    id: 4,
    active: process.env.ENABLE_BINANCE_TRADING ? true : false,
  },
});

export const ManageHeader = (props: IManageHeader) => {
  const { appLink } = React.useContext(FundsAppContext);
  const [menuSticked, setMenuSticked] = React.useState("");
  const actionSelectRef = React.useRef<HTMLInputElement>(null);

  const dropdownData = getDropdownData(props.tier);

  React.useEffect(() => {
    window.addEventListener("resize", handleSelectAttachment);
    window.addEventListener("scroll", handleSelectAttachment);

    return () => {
      window.removeEventListener("resize", handleSelectAttachment);
      window.removeEventListener("scroll", handleSelectAttachment);
    };
  });

  const headlineText = () =>
    props.currency === MAIN_CURRENCY
      ? MAIN_CURRENCY_LABEL
      : currencyFromUrl(props.location.search);

  const getCurrentBalance = (wallet: IWalletDetails) => {
    return wallet.current_balance;
  };

  const updateAction = (e: string) => {
    const url = cleanUrlFromAction(props.match.url, props.match.params.action);

    props.history.push({
      pathname: `${url}/${e.toLocaleLowerCase()}`,
      search: `?currency=${props.currency}`,
    });
  };

  const getBalance = (wallet: IWalletDetails) => {
    return wallet.balance;
  };

  const handleSelectAttachment = () => {
    const selectWrapper = actionSelectRef.current;
    if (!selectWrapper) return;
    const mobileView = document.documentElement.clientWidth < 1000;

    const elemOffset = menuSticked
      ? selectWrapper.parentElement!.offsetTop
      : selectWrapper.offsetTop;

    if (document.documentElement.scrollTop > elemOffset && mobileView) {
      setMenuSticked("sticky");
    } else {
      setMenuSticked("");
    }
  };

  const initialValue = () => {
    const action = props.match.params.action;
    if (!action) return "Select option";
    return action[0] + action.slice(1);
  };

  const availableActions = () =>
    props.availableActions.map((action) => dropdownData[action]);

  return (
    <header>
      <Header padding={"50 20"}>
        <div className={style.topHolder}>
          <h1 className={classNames(style.headline, "nostyle")}>
            {headlineText()}
          </h1>
          <Link to={appLink} className={style.back}>
            Back To Funds
          </Link>
        </div>
        <div className={style.content}>
          <div className={style.action}>
            <p className={style.actionText}>What you want to do?</p>
            <div
              className={classNames(style.dropdownHolder, style[menuSticked])}
              ref={actionSelectRef}
            >
              <Dropdown
                currency={props.currency}
                data={availableActions()}
                update={updateAction}
                initialValue={initialValue()}
              />
            </div>
          </div>
          <div className={style.balances}>
            <div className={style.balance}>
              <p className={style.title}>Your Balance</p>
              <span className={style.value}>
                <CurrencyElement
                  currency={props.currency}
                  value={getCurrentBalance(props.wallet)}
                />
              </span>
            </div>
            <div className={style.balance}>
              <p className={style.title}>Available Funds</p>
              <span className={style.value}>
                <CurrencyElement
                  currency={props.currency}
                  value={getBalance(props.wallet)}
                />
              </span>
            </div>
          </div>
        </div>
      </Header>
    </header>
  );
};

export default withRouter(ManageHeader);
